import React, { useState, useContext } from 'react';
import PropTypes from 'prop-types';
import { useStripe, useElements } from '@stripe/react-stripe-js';
import { useOffer } from '../../offer';
import { ApiResult } from '../../api';
import { BackgroundColorContainer, HideOnMobile } from '../../atoms';
import { PromoPill } from '../../molecules';
import { usePets } from '../../pets';
import { useReporter } from '../../reporter';
import {
  SHOW_ADD_ONS_KEY,
  SHOW_PROMO_CODE_KEY,
  useExperiment,
} from '../../experiments';
import { useOrderList } from '../../orders';
import {
  useSessionCustomer,
  useCartProducts,
  useCartSubscriptionProducts,
  useAddress,
} from '../../customer';
import { LoadingView, ServerSideErrorModal } from '../../components';
import { StripeContext } from '../../payment/stripe';
import { CheckoutForm } from '../CheckoutForm';
import { CheckoutHeader } from '../CheckoutHeader';
import { CHECKOUT_STEPS } from '../CheckoutHeader/constants';
import { createPaymentToken, usePayments } from '../../payment';
import { OnboardingContext } from '../OnboardingFlowContainer/context';
import { ACTIONS } from '../OnboardingFlowContainer/constants';
import { userCheckoutPurchased, userCompletedOrder } from './events';
import { ERROR_MESSAGE } from './constants';

export const CheckoutContainer = ({
  headerShippingAddress,
  labelFirstName,
  labelLastName,
  labelAddress,
  labelApt,
  labelZipCode,
  labelCity,
  labelState,
  stateOptions,
  labelPhoneNumber,
  labelSMSUpdates,
  labelSMSTerms,
  headerAccountInfo,
  labelEmail,
  labelPassword,
  labelConfirmation,
  headerIDMeForm,
  message,
  buttonImage,
  buttonAlt,
  headlineBillingInformation,
  labelWithStripe,
  labelNameCard,
  labelCardNumber,
  labelExpirationDate,
  labelCVC,
  placeholderNameCard,
  placeholderCardNumber,
  placeholderExpirationDate,
  placeholderCVC,
  orderSummary,
  recurringOrders,
  ctaText,
}) => {
  const offer = useOffer();
  const { send, pricePeriod } = useContext(OnboardingContext);
  const { stripeLoaded } = useContext(StripeContext);
  const stripe = useStripe();
  const elements = useElements();
  const reporter = useReporter();
  const { value: showAddons } = useExperiment(SHOW_ADD_ONS_KEY, false);
  const { value: showPromoCode } = useExperiment(SHOW_PROMO_CODE_KEY, false);
  const { listOrders } = useOrderList();
  const petsApi = usePets();
  const customerApi = useSessionCustomer();
  const addressApi = useAddress(customerApi.customer?.id); // TODO: remove opt chain when Mirage fixed
  const paymentsApi = usePayments(customerApi.customer?.id);
  const { productsListApi } = useCartProducts();
  const { cartApi, subscriptionProductsListApi } =
    useCartSubscriptionProducts();

  const [errorMessageInfo, setErrorMessageInfo] = useState(
    !stripeLoaded ? ERROR_MESSAGE.stripe : null,
  );
  const closeErrorModal = () => setErrorMessageInfo(null);

  const onSubmit = async data => {
    const updateCustomerResult = await ApiResult.callAsync(() =>
      customerApi.updateCustomer({
        first_name: data.firstName,
        last_name: data.lastName,
        email: data.email,
        is_sms: data.smsUpdates,
      }),
    );
    const setPasswordResult = await ApiResult.flatMapAsync(
      updateCustomerResult,
      () =>
        customerApi.createPassword({
          new_password: data.new_password,
          confirm_password: data.confirm_password,
        }),
    );
    const tokenResult = await ApiResult.flatMapAsync(setPasswordResult, () =>
      createPaymentToken({ elements, stripe }),
    );

    const paymentMethodResult = await ApiResult.flatMapAsync(
      tokenResult,
      token =>
        paymentsApi.create({
          last_four_digits: token.card.last4,
          provider_token: token.id,
          is_default: true,
        }),
    );
    const checkoutResult = await ApiResult.flatMapAsync(
      paymentMethodResult,
      () => customerApi.performCheckout(),
    );
    ApiResult.match(checkoutResult, {
      success: async () => {
        const orders = await listOrders();
        send(ACTIONS.toConfirmation({ orderId: orders[0].id }));
        try {
          await reporter.withPrefix('').tag(userCompletedOrder(orders));
        } catch (error) {
          reporter.error(error);
        }
        try {
          await reporter.tag(userCheckoutPurchased(orders));
        } catch (error) {
          reporter.error(error);
        }
      },
      error: {
        server: () => {
          setErrorMessageInfo(ERROR_MESSAGE.server);
        },
        paymentInvalid: () => {
          setErrorMessageInfo(ERROR_MESSAGE.paymentInvalid);
        },
        paymentInsufficientFunds: () => {
          setErrorMessageInfo(ERROR_MESSAGE.paymentInsufficientFunds);
        },
        unableToProcessOrder: () => {
          setErrorMessageInfo(ERROR_MESSAGE.unableToProcessOrder);
        },
        checkoutAddress: () => {
          setErrorMessageInfo(ERROR_MESSAGE.address);
        },
        passwordNotSet: () => {
          setErrorMessageInfo(ERROR_MESSAGE.passwordNotSet);
        },
        client: () => {
          setErrorMessageInfo(ERROR_MESSAGE.server);
        },
      },
    });

    return checkoutResult;
  };
  const applyPromoCodeHandler = async ({ newPromoCode }) => {
    const validateOfferCodeResult = await offer.validateOfferCode(newPromoCode);

    const newPromoCodeResult = await ApiResult.flatMapAsync(
      validateOfferCodeResult,
      () => offer.updateOfferCode(newPromoCode),
    );
    return newPromoCodeResult;
  };
  const removePromoCodeHandler = async () => {
    const validateOfferCodeResult = await offer.validateOfferCode(
      offer.defaultOfferCode,
    );
    const removePromoCodeResult = await ApiResult.flatMapAsync(
      validateOfferCodeResult,
      () => offer.updateOfferCode(offer.defaultOfferCode),
    );
    return removePromoCodeResult;
  };
  const isLoading =
    addressApi.isLoading ||
    cartApi.isLoading ||
    customerApi.isLoading ||
    paymentsApi.isLoading ||
    petsApi.isLoading ||
    productsListApi.isLoading ||
    subscriptionProductsListApi.isLoading;

  if (isLoading) return <LoadingView />;

  if (cartApi.error) return <LoadingView variant="error" />;

  const rightHeaderSegment = (
    <HideOnMobile>
      <PromoPill />
    </HideOnMobile>
  );

  return (
    <BackgroundColorContainer $bgColor="background.base">
      <CheckoutHeader
        currentStep={CHECKOUT_STEPS[CHECKOUT_STEPS.length - 1]} //* Last step will always be Checkout Page
        rightSegment={rightHeaderSegment}
        steps={CHECKOUT_STEPS}
      />
      <CheckoutForm
        headerShippingAddress={headerShippingAddress}
        labelFirstName={labelFirstName}
        labelLastName={labelLastName}
        labelAddress={labelAddress}
        labelApt={labelApt}
        labelZipCode={labelZipCode}
        labelCity={labelCity}
        labelState={labelState}
        stateOptions={stateOptions}
        labelPhoneNumber={labelPhoneNumber}
        labelSMSUpdates={labelSMSUpdates}
        labelSMSTerms={labelSMSTerms}
        headerAccountInfo={headerAccountInfo}
        labelEmail={labelEmail}
        labelPassword={labelPassword}
        labelConfirmation={labelConfirmation}
        headerIDMeForm={headerIDMeForm}
        message={message}
        buttonAlt={buttonAlt}
        buttonImage={buttonImage}
        headlineBillingInformation={headlineBillingInformation}
        labelNameCard={labelNameCard}
        labelCardNumber={labelCardNumber}
        labelExpirationDate={labelExpirationDate}
        labelCVC={labelCVC}
        labelWithStripe={labelWithStripe}
        placeholderNameCard={placeholderNameCard}
        placeholderCardNumber={placeholderCardNumber}
        placeholderExpirationDate={placeholderExpirationDate}
        placeholderCVC={placeholderCVC}
        ctaText={ctaText}
        onSubmit={onSubmit}
        address={addressApi.address}
        updateAddress={addressApi.update}
        cart={cartApi.cart}
        isValidating={cartApi.isValidating}
        refreshCart={cartApi.refreshCart}
        customer={customerApi.customer}
        pets={petsApi.pets}
        deletePet={petsApi.deletePet}
        products={productsListApi.products}
        subscriptionProducts={subscriptionProductsListApi.products}
        recurringOrders={recurringOrders}
        orderSummary={orderSummary}
        period={pricePeriod}
        showAddons={showAddons}
        showPromoCode={showPromoCode}
        applyPromoCode={applyPromoCodeHandler}
        removePromoCode={removePromoCodeHandler}
        isDefaultPromoCode={offer.code === offer.defaultOfferCode}
        currentOfferCode={offer.code}
        defaultOfferCode={offer.defaultOfferCode}
      />
      <ServerSideErrorModal
        shown={errorMessageInfo !== null}
        headerText={errorMessageInfo?.title ?? ''}
        errorMessage={errorMessageInfo?.message ?? ''}
        onCloseModal={closeErrorModal}
        variant={'secondary'}
        showCloseButton={true}
      />
    </BackgroundColorContainer>
  );
};

CheckoutContainer.displayName = 'CheckoutContainer';
CheckoutContainer.propTypes = {
  headerShippingAddress: PropTypes.string.isRequired,
  labelFirstName: PropTypes.string.isRequired,
  labelLastName: PropTypes.string.isRequired,
  labelAddress: PropTypes.string.isRequired,
  labelApt: PropTypes.string.isRequired,
  labelZipCode: PropTypes.string.isRequired,
  labelCity: PropTypes.string,
  labelState: PropTypes.string.isRequired,
  stateOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }).isRequired,
  ),
  labelPhoneNumber: PropTypes.string.isRequired,
  labelSMSUpdates: PropTypes.string.isRequired,
  labelSMSTerms: PropTypes.string.isRequired,
  headerAccountInfo: PropTypes.string.isRequired,
  labelEmail: PropTypes.string.isRequired,
  labelPassword: PropTypes.string.isRequired,
  labelConfirmation: PropTypes.string.isRequired,
  headerIDMeForm: PropTypes.string.isRequired,
  message: PropTypes.node,
  buttonImage: PropTypes.string.isRequired,
  buttonAlt: PropTypes.string.isRequired,
  headlineBillingInformation: PropTypes.string.isRequired,
  labelWithStripe: PropTypes.string.isRequired,
  labelNameCard: PropTypes.string.isRequired,
  labelCardNumber: PropTypes.string.isRequired,
  labelExpirationDate: PropTypes.string.isRequired,
  labelCVC: PropTypes.string.isRequired,
  placeholderNameCard: PropTypes.string.isRequired,
  placeholderCardNumber: PropTypes.string.isRequired,
  placeholderExpirationDate: PropTypes.string.isRequired,
  placeholderCVC: PropTypes.string.isRequired,
  orderSummary: PropTypes.object.isRequired,
  recurringOrders: PropTypes.shape({
    foodPacksText: PropTypes.string.isRequired,
  }).isRequired,
  ctaText: PropTypes.string.isRequired,
};
